import { supabase } from '@/lib/supabase';
import { DetailedBotAccount } from '@/types/trader';
import { findShadowBotId } from './shadow-bot-utils';

export const fetchDetailedBotAccounts = async (
  botId: string,
  userId: string
): Promise<DetailedBotAccount[]> => {
  console.log("Fetching connected accounts for bot:", botId);
  
  // First, get the bot accounts using original_bot_id for frontend display
  const { data: botAccounts, error: botAccountsError } = await supabase
    .from('bot_accounts')
    .select('id, bot_id, user_id, api_connection_id, volume_multiplier, status, accountid_trading, original_bot_id, live, is_reverse_position, added_date, created_at, updated_at')
    .eq('original_bot_id', botId) // Changed from bot_id to original_bot_id
    .eq('user_id', userId);
  
  if (botAccountsError) {
    console.error('Error fetching bot accounts:', botAccountsError);
    throw botAccountsError;
  }
  
  console.log("Found bot accounts:", botAccounts);
  
  if (!botAccounts || botAccounts.length === 0) {
    return [];
  }
  
  // For each bot account, fetch the related data
  const detailedAccounts: DetailedBotAccount[] = [];
  
  for (const account of botAccounts) {
    if (!account.api_connection_id) {
      console.log("No API connection ID for account:", account.id);
      continue;
    }
    
    // 1. Get API connection
    const { data: apiConnection, error: apiConnectionError } = await supabase
      .from('api_connections')
      .select('*')
      .eq('id', account.api_connection_id)
      .single();
    
    if (apiConnectionError) {
      console.error('Error fetching API connection:', apiConnectionError);
      continue;
    }
    
    if (!apiConnection) {
      console.log("No API connection found for ID:", account.api_connection_id);
      continue;
    }
    
    // 2. Get trading accounts for this API connection
    let tradingAccount = null;
    
    if (account.accountid_trading) {
      // If there's an accountid_trading, use it to find the trading account
      // Use both accountid_trading AND api_connection_id to avoid conflicts
      const { data, error } = await supabase
        .from('trading_accounts')
        .select('*')
        .eq('accountid_trading', account.accountid_trading)
        .eq('api_connection_id', account.api_connection_id)
        .maybeSingle(); // Use maybeSingle instead of single to avoid 406 errors
        
      if (!error && data) {
        tradingAccount = data;
        console.log('Found trading account by accountid_trading:', {
          accountid_trading: account.accountid_trading,
          api_connection_id: account.api_connection_id,
          account_number: data.number_account_trading,
          balance: data.balance_account
        });
      } else {
        console.log('Trading account not found by accountid_trading, trying fallback:', {
          accountid_trading: account.accountid_trading,
          api_connection_id: account.api_connection_id,
          error: error
        });
      }
    }
    
    // Fallback: get trading accounts for this API connection if we didn't find one above
    if (!tradingAccount) {
      const { data: tradingAccounts, error: tradingAccountsError } = await supabase
        .from('trading_accounts')
        .select('*')
        .eq('api_connection_id', apiConnection.id);
      
      if (tradingAccountsError) {
        console.error('Error fetching trading accounts:', tradingAccountsError);
        continue;
      }
      
      if (tradingAccounts && tradingAccounts.length > 0) {
        tradingAccount = tradingAccounts[0];
        console.log('Using first trading account as fallback:', {
          account_number: tradingAccount.number_account_trading,
          balance: tradingAccount.balance_account
        });
      }
    }
    
    // 3. Get CSP account using the csp_account_id from the API connection
    const { data: cspAccount, error: cspAccountError } = await supabase
      .from('csp_accounts')
      .select('csp_account_name, broker, prop_firm')
      .eq('id', apiConnection.csp_account_id)
      .single();
    
    if (cspAccountError) {
      console.error('Error fetching CSP account:', cspAccountError);
      continue;
    }
    
    // 4. Create detailed account by combining all data
    const detailedAccount: DetailedBotAccount = {
      ...account,
      trading_account: tradingAccount || undefined,
      api_connection: {
        ...apiConnection,
        trading_accounts: tradingAccount ? [tradingAccount] : []
      },
      csp_account: cspAccount || undefined
    };
    
    console.log("Processed detailed account:", {
      accountId: detailedAccount.id,
      tradingAccountNumber: tradingAccount?.number_account_trading || 'Unknown',
      apiName: apiConnection.api_name,
      cspName: cspAccount?.csp_account_name || 'Unknown',
      isReverse: detailedAccount.is_reverse_position,
      balance: tradingAccount?.balance_account || 0
    });
    
    detailedAccounts.push(detailedAccount);
  }
  
  console.log("Returning detailed accounts:", detailedAccounts.length);
  return detailedAccounts;
};

export const toggleAccountConnection = async (accountId: string, isConnected: boolean) => {
  const { error } = await supabase
    .from('bot_accounts')
    .update({ status: isConnected ? 'connected' : 'disconnected' })
    .eq('id', accountId);
  
  if (error) throw error;
  
  return { success: true };
};

export const addBotAccount = async (accountData: {
  bot_id: string;
  user_id: string;
  api_connection_id: string;
  volume_multiplier: number;
  status?: 'connected' | 'disconnected';
  is_reverse_position?: boolean;
}) => {
  const isReversePosition = accountData.is_reverse_position || false;
  const originalBotId = accountData.bot_id;
  
  // Determine bot_id for backend routing
  let backendBotId = originalBotId; // Default to original
  
  if (isReversePosition) {
    // For reverse positions, try to find shadow bot
    const shadowBotId = await findShadowBotId(originalBotId);
    if (shadowBotId) {
      backendBotId = shadowBotId;
    } else {
      console.warn('Shadow bot not found for reverse position, using original bot');
    }
  }

  // Fetch bot data to get signal tokens (using backend bot ID)
  const { data: bot, error: botError } = await supabase
    .from('bots')
    .select('signal_token, reverse_signal_token')
    .eq('id', backendBotId)
    .single();

  if (botError) throw botError;

  if (!bot) {
    throw new Error('Bot not found');
  }

  // For signal token, use the appropriate token from the backend bot
  const signalToken = bot.signal_token;

  const { data, error } = await supabase
    .from('bot_accounts')
    .insert({
      bot_id: backendBotId, // For backend routing
      original_bot_id: originalBotId, // For frontend display
      user_id: accountData.user_id,
      api_connection_id: accountData.api_connection_id,
      volume_multiplier: accountData.volume_multiplier,
      status: accountData.status || 'connected',
      is_reverse_position: isReversePosition,
      signal_token: signalToken
    })
    .select('id')
    .single();
    
  if (error) throw error;
  
  return data;
};

export const deleteBotAccount = async (accountId: string) => {
  const { error } = await supabase
    .from('bot_accounts')
    .delete()
    .eq('id', accountId);
  
  if (error) throw error;
  
  return { success: true };
};
